import type { FunctionComponent, ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { Text } from '@atlaskit/primitives';

import { useMcpContext } from '../McpContextProvider';
import { bitbucketWorkspaceSelectorMessages } from './messages';
import MultiSelect from '../../shared/MultiSelect/MultiSelect';
import SiteTile from '../../../Tile/SiteTile/SiteTile';
import {
  getAvatarIconColor,
  getSiteTileColor,
} from '../../../Tile/siteTileColors';
import type { BitbucketWorkspace } from '#definitions/consent-info';

const BITBUCKET_SELECT_INPUT_ID =
  'oauth-consent-mcp-bitbucket-workspace-select';

/**
 * Multi-select Bitbucket workspace picker for the MCP consent screen.
 *
 * Rendered above the Atlassian site selector when the
 * `oauth-consent_bitbucket-mcp-workspace` gate is on and /consent/info returned
 * at least one workspace in `availableBitbucketWorkspaces`. An absent or empty
 * list hides the selector entirely. Each selected workspace's ARI is submitted
 * as an additional `selectedWorkspace` param on Accept (see McpFlowParameters).
 *
 * MCP.tsx additionally skips rendering this selector while the flow is in an
 * error state (failed policy check or no available sites).
 */
const BitbucketWorkspaceSelector: FunctionComponent =
  (): ReactElement | null => {
    const {
      isBitbucketWorkspaceAccessEnabled,
      availableBitbucketWorkspaces,
      selectedBitbucketWorkspaces,
      setSelectedBitbucketWorkspaces,
    } = useMcpContext();
    const { formatMessage } = useIntl();

    if (
      !isBitbucketWorkspaceAccessEnabled ||
      availableBitbucketWorkspaces.length === 0
    ) {
      return null;
    }

    return (
      <MultiSelect<BitbucketWorkspace, string>
        inputId={BITBUCKET_SELECT_INPUT_ID}
        items={availableBitbucketWorkspaces}
        value={selectedBitbucketWorkspaces.map((ws) => ws.ari)}
        toValue={(ws) => ws.ari}
        getLabel={(ws) => ws.name}
        getIcon={(ws) => <SiteTile avatarUrl={ws.avatar} />}
        getChipColor={(ws) =>
          getAvatarIconColor(ws.avatar) ?? getSiteTileColor(ws.name)
        }
        placeholder={formatMessage(
          bitbucketWorkspaceSelectorMessages.placeholder,
        )}
        renderLabel={() => (
          <Text weight="medium" color="color.text.subtle">
            {formatMessage(bitbucketWorkspaceSelectorMessages.label)}
          </Text>
        )}
        onChange={(_aris, items) => setSelectedBitbucketWorkspaces(items)}
      />
    );
  };

export default BitbucketWorkspaceSelector;
